import CasTextField, {
  ICasTextField,
} from 'src/compass/shared/components/CasTextField/CasTextField';

type CustomerSearchFieldProps = ICasTextField & {
  // CasTable hands these to every `component` filter; a text box has no use for them.
  includeAllOption?: boolean;
  includeNoneOption?: boolean;
  includeEmptyOption?: boolean;
  // CasFilterChips renders a component filter in display mode for its chip text.
  mode?: 'display';
};

/*
  A `component` filter rather than a plain text one only so it can size itself to its
  label. CasTable leaves a text filter at MUI's default (~200px), which cuts this label
  off with an ellipsis whenever the box is empty, and gives it no way to ask for more.
  (Sizing text filters to their label inside CasTable would widen other pages' filters
  too.)
*/
const CustomerSearchField = ({
  includeAllOption,
  includeNoneOption,
  includeEmptyOption,
  mode,
  fullWidth,
  sx,
  ...props
}: CustomerSearchFieldProps) => {
  if (mode === 'display') return <>{props.value}</>;

  return (
    <CasTextField
      {...props}
      fullWidth={fullWidth}
      // The Filters drawer passes its full width through. On the toolbar, CasTable's
      // label-based estimate (8px a character) would leave the label ~3px to spare -
      // too little to survive another machine's font rendering.
      sx={fullWidth ? sx : { minWidth: 264 }}
      InputLabelProps={{
        sx: {
          // Once a value is typed the theme floats the label at 16px scaled to 75%, but
          // still clips it to the width of the box it no longer sits in, which cuts this
          // label off. Let it run to the width it is drawn at, as newer MUI versions do.
          '&.MuiInputLabel-shrink': { maxWidth: 'calc(133% - 32px)' },
        },
      }}
      // This search scans names three ways plus the paging count, so it waits for a
      // pause in typing rather than firing per keystroke - and longer than the app
      // default, which split normal typing into several requests here.
      debounce={400}
    />
  );
};

/*
  The search filter on every tab of Risk Management > Customers (Customers, Touchpoints,
  Handoffs, Unassigned). All four list Utilities/EntityTouchpoint, whose SearchName
  (EntityTouchpointListRequestBo) matches the customer's name, its contacts' names, and
  its accounts' names - so the label says so, since a row can match on an account the
  grid never shows. It matches exactly as the page's search box does (a substring, or a
  5 digit account number), so it takes any character the box does. One definition keeps
  the four tabs from drifting apart.
*/
const customerSearchFilter = {
  type: 'component',
  name: 'searchName',
  label: 'Search Name, Contacts, Accounts',
  component: CustomerSearchField,
};

export default customerSearchFilter;
